import { getLanguage, location } from 'cdui-js';
import { applyH5Lang } from '../../../../i18n';
import { goBackByBrowser } from '../../../../lib/browser';
import {
  initRNBridge,
  postToApp,
  registerRNHandler,
  applyRNUserToken,
  notifySessionRNTokenReady,
  type RNMessage,
} from '../../../../lib/rnBridge';
import { applySafeArea, applyTheme, initLineTheme, commonState } from '../../../../stores/common';
import { applyAppRequestDevice } from '../../../../lib/appRequestDevice';
import { applyUserInfo, userState } from '../../../../stores/user';

/** 入金页不改写的业务 query，其余原样留着往下传；isBackToApp 仅跳转时显式写入 */
const BUSINESS_QUERY = new Set([
  'currency',
  'accountNum',
  'payeeBankAccountNum',
  'payeeBankCode',
  'payeeBankName',
  'isBackToApp',
]);

export const DEPOSIT_TOKEN_REQUEST = 'WEB_TO_APP_TOKEN';
export const DEPOSIT_TOKEN_RESPONSE = 'APP_TO_WEB_TOKEN';
/** 与 TOKEN 二选一。回包读 extra，外壳参数一并落入本地，供资金记录补齐 */
export const H5_SESSION_REQUEST = 'WEB_TO_APP_H5Session_Request';
export const H5_SESSION_RESPONSE = 'APP_TO_WEB_H5Session';

/** 入金 / 出金 / 兑换收到 token 后落下的外壳，资金记录页优先读它 */
const APP_SHELL_KEY = 'h5-app-shell';

/** WebView 内等 App 回 token 的最长时间 */
const TOKEN_TIMEOUT_MS = 15000;

export type DepositAppQuery = Record<string, string>;

export type DepositMode = '' | 'security' | 'virtual';
export type DepositModeType = '' | 'C' | 'M';

export type DepositEntry = {
  mode: DepositMode;
  modeType: DepositModeType;
};

const emptyDepositEntry = (): DepositEntry => ({ mode: '', modeType: '' });

let depositEntry: DepositEntry = emptyDepositEntry();

export const getDepositEntry = (): DepositEntry => ({ ...depositEntry });

/**
 * extra.mode 定证券或虚拟。modeType 只在证券里决定默认现金 C 还是孖展 M，不过滤列表。
 * 证券缺省当 C。虚拟不要补成 C 或 M。
 */
export const applyDepositExtra = (extra: unknown) => {
  const raw = extra && typeof extra === 'object' ? (extra as Record<string, unknown>) : {};
  const mode = String(raw.mode ?? '').trim();
  const modeType = String(raw.modeType ?? '').trim();
  depositEntry = {
    mode: mode === 'security' || mode === 'virtual' ? mode : '',
    modeType: mode === 'security' ? (modeType === 'M' ? 'M' : 'C') : '',
  };
};

/** App 预拉：单币种可兑金额（对齐 GET cashBalancesByCurrency） */
export type ExchangeCashBalanceByCurrency = {
  accountNum?: string;
  currency?: string;
  /** 可兑金额 — 与接口 data 一致 */
  balance?: number;
};

/** App 预拉的货币兑换首屏包，挂在 APP_TO_WEB_TOKEN.payload.exchange */
export type ExchangePrefetchPayload = {
  accountNum?: string;
  stockAccountsMeta?: unknown;
  /** 兑出币可兑金额；替代旧 stockAccountsWithdrawalBal */
  cashBalanceByCurrency?: ExchangeCashBalanceByCurrency;
  availableCurrencies?: unknown;
  rates?: { from?: string; to?: string; rate?: number | string };
};

export type DepositAppTokenPayload = {
  token?: string;
  authorization?: string;
  userId?: string | number;
  /** App 下发的用户资料；含 userMsg.username，出金封包要用 */
  userInfo?: unknown;
  extra?: { mode?: string; modeType?: string };
  /** 货币兑换页首屏数据；入金/出金可忽略 */
  exchange?: ExchangePrefetchPayload;
  lang?: string;
  theme?: string;
  scheme?: string;
  riseFallMode?: string;
  safeHeight?: number | string;
  safeAreaTop?: number | string;
  statusBarHeight?: number | string;
  safeTop?: number | string;
  safeAreaBottom?: number | string;
  safeBottom?: number | string;
  safeArea?: { top?: number; bottom?: number; left?: number; right?: number };
  devicecode?: string;
  deviceCode?: string;
  devicemodel?: string;
  deviceModel?: string;
  deviceversion?: string;
  deviceVersion?: string;
  platform?: string;
  [key: string]: unknown;
};

/** 兑换页消费一次；旧 App 无 exchange 时为 null，H5 自行拉读接口 */
let exchangePrefetch: ExchangePrefetchPayload | null = null;

export const takeExchangePrefetch = (): ExchangePrefetchPayload | null => {
  const data = exchangePrefetch;
  exchangePrefetch = null;
  return data;
};

const summarizeExchange = (raw: unknown) => {
  if (!raw || typeof raw !== 'object') return null;
  const ex = raw as ExchangePrefetchPayload;
  const meta = Array.isArray(ex.stockAccountsMeta) ? ex.stockAccountsMeta.length : 0;
  const bal = ex.cashBalanceByCurrency;
  const currencies = Array.isArray(ex.availableCurrencies) ? ex.availableCurrencies : null;
  return {
    accountNum: ex.accountNum ?? '',
    metaCount: meta,
    cashBalanceByCurrency: bal
      ? { accountNum: bal.accountNum ?? '', currency: bal.currency ?? '', balance: bal.balance }
      : null,
    currencies,
    rates: ex.rates ?? null,
  };
};

const applyExchangePrefetch = (raw: unknown) => {
  if (!raw || typeof raw !== 'object') {
    exchangePrefetch = null;
    console.log('[APP_TO_WEB_TOKEN] exchange 缺失，首屏将自拉读接口');
    return;
  }
  exchangePrefetch = raw as ExchangePrefetchPayload;
  console.log('[APP_TO_WEB_TOKEN] exchange 已落库（首屏预拉）', summarizeExchange(raw), raw);
};

let entryQuery: DepositAppQuery | null = null;
let tokenBoot: Promise<void> | null = null;
/** 同一份应用结果只写一次。拉汇率、改金额再次进入时直接跳过 */
let lastAppliedQueryKey = '';

const readSearch = (): DepositAppQuery => {
  const params: DepositAppQuery = {};
  try {
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!key || key in params) return;
      params[key] = value;
    });
  } catch {
    return {};
  }
  return params;
};

/** 落地 URL 只抓一次。后面 routeTo 会丢掉 theme / lang / 安全高度 */
export const snapshotDepositAppQuery = () => {
  if (entryQuery) return entryQuery;
  entryQuery = readSearch();
  console.log('[h5-deposit] URL query', entryQuery);
  return entryQuery;
};

export const getDepositAppQuery = () => ({ ...snapshotDepositAppQuery() });

const px = (value: unknown) => {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

/** App 可能下发 cn / us，也兼容完整线色码 */
const normalizeRiseFallMode = (raw?: string | null) => {
  if (!raw) return undefined;
  const value = String(raw).trim().toLowerCase();
  if (!value) return undefined;
  if (value === 'cn' || value === 'redup_greendown') return 'redup_greendown';
  if (value === 'us' || value === 'greenup_reddown') return 'greenup_reddown';
  return value;
};

const markPlatformRn = () => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.add('platform-rn');
  document.body?.classList.add('platform-rn');
};

const applyInsets = (top?: number, bottom?: number, left?: number, right?: number) => {
  if (top == null && bottom == null && left == null && right == null) return;
  const prev = { ...commonState.safeAreaInsets };
  applySafeArea({
    top: top ?? prev.top,
    bottom: bottom ?? prev.bottom,
    left: left ?? prev.left,
    right: right ?? prev.right,
  });
};

const pick = (query: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = px(query[key]);
    if (value != null) return value;
  }
  return undefined;
};

/** 用本次会写进状态的字段做指纹，不读 commonState，避免渲染期订阅 */
const appliedQueryKey = (query: DepositAppQuery) =>
  JSON.stringify({
    theme: query.theme || query.scheme || '',
    lang: query.lang || '',
    riseFallMode: normalizeRiseFallMode(query.riseFallMode) || '',
    platform: query.platform || '',
    safeTop: pick(query, ['safeHeight', 'safeAreaTop', 'statusBarHeight', 'safeTop']) ?? '',
    safeBottom: pick(query, ['safeAreaBottom', 'safeBottom']) ?? '',
  });

/** URL：theme / scheme、lang、涨跌色、安全高度、platform（首屏先铺一层） */
export const applyDepositAppQuery = (query: DepositAppQuery = getDepositAppQuery()) => {
  const key = appliedQueryKey(query);
  if (key === lastAppliedQueryKey) return;
  lastAppliedQueryKey = key;

  const theme = query.theme || query.scheme;
  if (theme) applyTheme(theme);
  if (query.lang) void applyH5Lang(query.lang);

  const riseFallMode = normalizeRiseFallMode(query.riseFallMode);
  if (riseFallMode) initLineTheme(riseFallMode);

  if (query.platform === 'rn') markPlatformRn();

  applyInsets(
    pick(query, ['safeHeight', 'safeAreaTop', 'statusBarHeight', 'safeTop']),
    pick(query, ['safeAreaBottom', 'safeBottom']),
  );

  console.log('[h5-deposit] applied app query', {
    theme: theme || commonState.theme,
    lang: query.lang || '',
    riseFallMode: riseFallMode || commonState.lineTheme,
    platform: query.platform || '',
    safeTop: commonState.safeAreaInsets.top,
    safeBottom: commonState.safeAreaInsets.bottom,
  });
};

export const appendDepositAppQuery = (params: URLSearchParams) => {
  Object.entries(snapshotDepositAppQuery()).forEach(([key, value]) => {
    if (!key || !value || BUSINESS_QUERY.has(key) || params.has(key)) return;
    params.set(key, value);
  });
};

/** 仅 hideNav=1 表示 App 已画顶栏。platform=rn 单独出现时仍由 H5 自己画 */
export const isFundHideNav = () => {
  void (location.query as Record<string, string> | undefined)?.hideNav;
  try {
    return new URLSearchParams(window.location.search).get('hideNav') === '1';
  } catch {
    return false;
  }
};

export type FundRecordsUrlOpts = {
  accountNum?: string;
  /** 成功弹窗「查看记录」为 true：资金列表返回时回 App */
  isBackToApp?: boolean;
};

/** 资金记录地址：type / 账户 / mode·modeType / isBackToApp，并带上外壳 query */
export const fundRecordsUrl = (type: string, opts?: string | FundRecordsUrlOpts) => {
  const options: FundRecordsUrlOpts = typeof opts === 'string' ? { accountNum: opts } : { ...(opts || {}) };
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  const num = String(
    options.accountNum ?? (location.query as Record<string, string> | undefined)?.accountNum ?? '',
  ).trim();
  if (num) params.set('accountNum', num);
  const entry = getDepositEntry();
  if (entry.mode) params.set('mode', entry.mode);
  if (entry.modeType) params.set('modeType', entry.modeType);
  if (options.isBackToApp) params.set('isBackToApp', '1');
  appendDepositAppQuery(params);
  return `/h5/fund-records?${params.toString()}`;
};

const readSearchParam = (key: string) => {
  void (location.query as Record<string, string> | undefined)?.[key];
  try {
    return new URLSearchParams(window.location.search).get(key) || '';
  } catch {
    return '';
  }
};

/** 当前页是否要求返回时直接回 App（成功「查看记录」带入） */
export const isFundBackToApp = () => readSearchParam('isBackToApp') === '1';

export const getBackToAppUrl = () => {
  const fromUrl = readSearchParam('backToAppUrl').trim();
  if (fromUrl) return fromUrl;
  return String(getDepositAppQuery().backToAppUrl || '').trim();
};

const inAppWebView = () => typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;

/** 回 App：有 backToAppUrl 时开原生指定页（WEB_TO_APP_OpenNative），否则出栈（WEB_TO_APP_GoBack）。
 *  不能统一用 GoBack：native 收到 GoBack 会 webView.goBack if canGoBack else close，
 *  fund-records 有 history 可退（入金 Home 在栈底），canGoBack=true 会被 native 退到入金 Home
 *  而不是关 WebView 回 App。OpenNative 让 native 直接打开 backToAppUrl 原生页。 */
export const leaveToApp = () => {
  const inApp = inAppWebView();
  const nativePath = getBackToAppUrl();
  console.log('[fund-records] leaveToApp', {
    inAppWebView: inApp,
    hasReactNativeWebView: typeof window !== 'undefined' && !!(window as any).ReactNativeWebView,
    nativePath,
    isBackToApp: isFundBackToApp(),
    url: window.location.href,
    historyLength: typeof history !== 'undefined' ? history.length : -1,
  });
  if (inApp) {
    if (nativePath) {
      postToApp({
        type: 'WEB_TO_APP_OpenNative',
        timestamp: Date.now(),
        payload: { nativePath, url: nativePath, backToAppUrl: nativePath },
      });
      console.log('[fund-records] leaveToApp 已 postToApp WEB_TO_APP_OpenNative', { nativePath });
    } else {
      postToApp({ type: 'WEB_TO_APP_GoBack', timestamp: Date.now() });
      console.log('[fund-records] leaveToApp 已 postToApp WEB_TO_APP_GoBack（无 backToAppUrl）');
    }
    return;
  }
  console.log('[fund-records] leaveToApp 走 goBackByBrowser（非 WebView）');
  goBackByBrowser();
};

/** 资金记录返回：isBackToApp=1 回 App，否则上一页 */
export const leaveFundRecords = () => {
  if (isFundBackToApp()) {
    leaveToApp();
    return;
  }
  goBackByBrowser();
};

const pickToken = (payload: DepositAppTokenPayload) =>
  String(payload.authorization || payload.token || '').trim();

type H5AppShell = {
  theme: string;
  lang: string;
  riseFallMode: string;
  platform: string;
  safeHeight: string;
  safeAreaBottom: string;
};

const emptyAppShell = (): H5AppShell => ({
  theme: '',
  lang: '',
  riseFallMode: '',
  platform: '',
  safeHeight: '',
  safeAreaBottom: '',
});

const textOf = (value: unknown) => (value == null ? '' : String(value).trim());

const firstText = (...values: unknown[]) => {
  for (const value of values) {
    const text = textOf(value);
    if (text) return text;
  }
  return '';
};

const firstPx = (...values: unknown[]) => {
  for (const value of values) {
    const n = px(value);
    if (n != null) return n;
  }
  return undefined;
};

const readAppShell = (): H5AppShell => {
  try {
    const raw = localStorage.getItem(APP_SHELL_KEY);
    if (!raw) return emptyAppShell();
    const data = JSON.parse(raw) as Partial<H5AppShell>;
    return {
      theme: textOf(data?.theme),
      lang: textOf(data?.lang),
      riseFallMode: textOf(data?.riseFallMode),
      platform: textOf(data?.platform),
      safeHeight: textOf(data?.safeHeight),
      safeAreaBottom: textOf(data?.safeAreaBottom),
    };
  } catch {
    return emptyAppShell();
  }
};

const shellHasValue = (shell: H5AppShell) =>
  Boolean(
    shell.theme || shell.lang || shell.riseFallMode || shell.platform || shell.safeHeight || shell.safeAreaBottom,
  );

const saveAppShell = (shell: H5AppShell) => {
  if (!shellHasValue(shell)) return;
  try {
    localStorage.setItem(APP_SHELL_KEY, JSON.stringify(shell));
  } catch {
    // ignore
  }
};

const shellToQuery = (shell: H5AppShell): DepositAppQuery => {
  const query: DepositAppQuery = {};
  if (shell.theme) query.theme = shell.theme;
  if (shell.lang) query.lang = shell.lang;
  if (shell.riseFallMode) query.riseFallMode = shell.riseFallMode;
  if (shell.platform) query.platform = shell.platform;
  if (shell.safeHeight) query.safeHeight = shell.safeHeight;
  if (shell.safeAreaBottom) query.safeAreaBottom = shell.safeAreaBottom;
  return query;
};

/** 回包字段优先，其次首屏 URL，最后保留上次落下的本地值 */
const mergeAppShell = (payload: DepositAppTokenPayload): H5AppShell => {
  const query = snapshotDepositAppQuery();
  const prev = readAppShell();
  const safeTop = firstPx(
    payload.safeHeight,
    payload.safeAreaTop,
    payload.statusBarHeight,
    payload.safeTop,
    payload.safeArea?.top,
    pick(query, ['safeHeight', 'safeAreaTop', 'statusBarHeight', 'safeTop']),
    prev.safeHeight,
  );
  const safeBottom = firstPx(
    payload.safeAreaBottom,
    payload.safeBottom,
    payload.safeArea?.bottom,
    pick(query, ['safeAreaBottom', 'safeBottom']),
    prev.safeAreaBottom,
  );
  return {
    theme: firstText(payload.theme, payload.scheme, query.theme, query.scheme, prev.theme),
    lang: firstText(payload.lang, query.lang, prev.lang),
    riseFallMode: firstText(
      normalizeRiseFallMode(payload.riseFallMode),
      normalizeRiseFallMode(query.riseFallMode),
      prev.riseFallMode,
    ),
    platform: firstText(payload.platform, query.platform, prev.platform),
    safeHeight: safeTop == null ? '' : String(safeTop),
    safeAreaBottom: safeBottom == null ? '' : String(safeBottom),
  };
};

/** 写入 token、设备头和 extra，并把主题 / 语言 / 安全高度落到本地 */
const applyTokenPayload = (payload: DepositAppTokenPayload) => {
  const token = pickToken(payload);
  if (token) {
    applyRNUserToken(token);
    // 与 UserToken 同源标记本会话就绪，敏感图 waitForRNToken 可立刻放行
    notifySessionRNTokenReady(token);
    userState.isLogin = true;
  }
  applyAppRequestDevice(payload);
  applyDepositExtra(payload.extra);
  applyExchangePrefetch(payload.exchange);

  const userInfo =
    payload.userInfo && typeof payload.userInfo === 'object'
      ? (payload.userInfo as Record<string, unknown>)
      : null;
  if (userInfo) applyUserInfo(userInfo);

  const userId = payload.userId;
  if (userId != null && String(userId).trim()) {
    userState.id = String(userId);
  }

  const shell = mergeAppShell(payload);
  saveAppShell(shell);
  applyDepositAppQuery(shellToQuery(shell));

  console.log('[h5-deposit] applied session', {
    hasToken: Boolean(token),
    userId: userState.id,
    userName: userState.userName,
    hasUserInfo: Boolean(userInfo),
    hasExchange: Boolean(payload.exchange && typeof payload.exchange === 'object'),
    deviceCode: payload.deviceCode || payload.devicecode || '',
    deviceModel: payload.deviceModel || payload.devicemodel || '',
    deviceVersion: payload.deviceVersion || payload.deviceversion || '',
    platform: payload.platform || '',
    mode: depositEntry.mode,
    modeType: depositEntry.modeType,
    theme: shell.theme,
    lang: shell.lang,
    safeHeight: shell.safeHeight,
    safeAreaBottom: shell.safeAreaBottom,
  });
};

const isSessionMessage = (message: RNMessage) =>
  message?.type === DEPOSIT_TOKEN_RESPONSE || message?.type === H5_SESSION_RESPONSE;

const newRequestId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `deposit-${Date.now().toString(36)}`;

/**
 * 进页向 App 要会话：发 WEB_TO_APP_TOKEN，等 APP_TO_WEB_TOKEN 或 APP_TO_WEB_H5Session。
 * 回包须带 token 才放行后续 API，并落下主题、语言和安全高度。
 * 非 WebView（浏览器调试）直接放行，走本地登录 token。
 */
export const requestDepositToken = () => {
  if (tokenBoot) return tokenBoot;

  if (typeof window === 'undefined' || !(window as any).ReactNativeWebView) {
    console.log('[h5-deposit] not in WebView, skip WEB_TO_APP_TOKEN');
    tokenBoot = Promise.resolve();
    return tokenBoot;
  }

  initRNBridge();
  const requestId = newRequestId();

  tokenBoot = new Promise<void>((resolve, reject) => {
    let done = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let unsubHandler: (() => void) | undefined;

    const cleanup = () => {
      if (timer !== undefined) clearTimeout(timer);
      unsubHandler?.();
      window.removeEventListener(DEPOSIT_TOKEN_RESPONSE, onCustomEvent);
      window.removeEventListener(H5_SESSION_RESPONSE, onCustomEvent);
    };

    const finishOk = (payload: DepositAppTokenPayload, message?: RNMessage) => {
      if (done) return;
      done = true;
      cleanup();
      console.log('[APP_TO_WEB_TOKEN] 收到回包（首次会话握手）', {
        type: message?.type || DEPOSIT_TOKEN_RESPONSE,
        requestId: message?.requestId || requestId,
        hasToken: Boolean(pickToken(payload)),
        hasExchange: Boolean(payload.exchange && typeof payload.exchange === 'object'),
        exchange: summarizeExchange(payload.exchange),
        extra: payload.extra ?? null,
        platform: payload.platform || '',
        payload,
      });
      applyTokenPayload(payload);
      console.log('[APP_TO_WEB_TOKEN] token ready', {
        hasExchange: Boolean(exchangePrefetch),
        mode: depositEntry.mode,
        modeType: depositEntry.modeType,
      });
      resolve();
    };

    const finishFail = (reason: string) => {
      if (done) return;
      done = true;
      cleanup();
      console.warn('[h5-deposit] token failed:', reason);
      reject(new Error(reason));
    };

    const onPayload = (message: RNMessage | undefined) => {
      if (!message || !isSessionMessage(message)) return;
      if (message.requestId && message.requestId !== requestId) {
        console.log('[APP_TO_WEB_TOKEN] 忽略 requestId 不匹配的回包', {
          expect: requestId,
          got: message.requestId,
          type: message.type,
        });
        return;
      }
      const payload = (message.payload || {}) as DepositAppTokenPayload;
      if (!pickToken(payload)) {
        applyDepositExtra(payload.extra);
        console.warn('[APP_TO_WEB_TOKEN] 回包缺 token，继续等待', message.type, {
          mode: depositEntry.mode,
          modeType: depositEntry.modeType,
          hasExchange: Boolean(payload.exchange),
        });
        return;
      }
      finishOk(payload, message);
    };

    const onCustomEvent = (event: Event) => {
      onPayload((event as CustomEvent<RNMessage>).detail);
    };

    unsubHandler = registerRNHandler((message) => onPayload(message));
    window.addEventListener(DEPOSIT_TOKEN_RESPONSE, onCustomEvent);
    window.addEventListener(H5_SESSION_RESPONSE, onCustomEvent);

    console.log('[WEB_TO_APP_TOKEN] 发起首次会话请求', { requestId });
    postToApp({
      type: DEPOSIT_TOKEN_REQUEST,
      requestId,
      timestamp: Date.now(),
    });

    timer = setTimeout(() => {
      finishFail(`APP_TO_WEB_TOKEN timeout ${TOKEN_TIMEOUT_MS}ms`);
    }, TOKEN_TIMEOUT_MS);
  }).catch((error) => {
    // 失败后允许再次 request（避免永久卡死）
    tokenBoot = null;
    throw error;
  });

  return tokenBoot;
};

/** 先用 URL 铺首屏，再 WEB_TO_APP_TOKEN → APP_TO_WEB_TOKEN，成功后才打入金 API */
export const bootDepositApp = () => {
  applyDepositAppQuery(snapshotDepositAppQuery());
  return requestDepositToken();
};

/** 资金记录优先本地：h5-app-shell，缺的再补 theme / lang / lineTheme */
const readFundRecordsShell = (): DepositAppQuery => {
  const query = shellToQuery(readAppShell());
  if (!query.theme) {
    try {
      const theme = localStorage.getItem('theme');
      if (theme) query.theme = theme;
    } catch {
      // ignore
    }
  }
  if (!query.lang) {
    const lang = getLanguage();
    if (lang) query.lang = lang;
  }
  if (!query.riseFallMode) {
    try {
      const lineTheme = localStorage.getItem('lineTheme');
      if (lineTheme) query.riseFallMode = lineTheme;
    } catch {
      // ignore
    }
  }
  return query;
};

/** 渲染前同步套上本地外壳。URL 已应用过时仍以本地字段为准 */
export const applyFundRecordsShell = () => {
  const stored = readFundRecordsShell();
  if (!Object.keys(stored).length) return;
  if (appliedQueryKey(stored) === lastAppliedQueryKey) return;
  lastAppliedQueryKey = '';
  applyDepositAppQuery(stored);
};

/**
 * 资金记录：先要 token，再用本地主题 / 语言 / 安全高度盖过缺失的 URL。
 * 本地有的字段优先，避免记录页 URL 把首屏外壳冲掉。
 */
export const bootFundRecordsApp = () => {
  const boot = bootDepositApp();
  applyFundRecordsShell();
  return boot;
};
